import React from 'react';
import PropTypes from 'prop-types';
import { useApi } from 'hooks';
import { useTranslation } from 'react-i18next';
import UiConfirmDialog from 'ui-components/ui-confirm-dialog/UiConfirmDialog';
import UiButton from 'ui-components/ui-button/UiButton';
import UiIcon from 'ui-components/ui-icon/UiIcon';
import { UiErrors } from 'components/error/Error';
import { toast } from 'lib';

const UiTableDeleteOptionModal = ({
  className,
  action,
  updateData,
  children,
  selected = [],
  onSuccess = null,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const {
    loadingRemove: loading,
    errorRemove: error,
    getApiParams,
    clearData,
  } = useApi();
  const selectedIds = selected.map((i) => i.id) || [];
  const handelConfirm = () => {
    const { path, params, dataModel, method } = getApiParams(action?.apiKey);
    const renderData = ({ data: dataValue, meta: metaValue }) => {
      return {
        data: dataValue.filter((i) => !selectedIds.includes(i?.id)),
        meta: { ...metaValue, total: metaValue?.total - selected?.length },
      };
    };

    method(
      path?.replace(':id', action?.id ? action?.id : selectedIds.join(',')),
      { ...params, ...action?.params },
      dataModel
    )
      .then(() => {
        updateData('data', renderData);
        if (onSuccess) onSuccess();
        toast({
          title: (
            <div>
              {action?.title} {selected.length > 1 ? t('common:plural') : ''}
            </div>
          ),
          description: (
            <div>
              {t('common:error.removeConfirmation.description', {
                title: action?.title,
                count: selected.length,
              })}
            </div>
          ),
          type: 'success',
        });
        if (typeof action.onSuccess === 'function') action.onSuccess(selected);
        if (onSuccess) setOpen(false);
      })
      .catch(() => {})
      .then(() => {});
  };

  return (
    <>
      <div
        className={className}
        onClick={() => setOpen(true)}
        data-testid={dataTestId}
      >
        {children}
      </div>
      <UiConfirmDialog
        open={open}
        data-testid={`${dataTestId}-dialog`}
        actions={
          <>
            <UiButton
              variant='outlined'
              color='secondary'
              startIcon={<UiIcon icon='ri-delete-bin-7-line' color='#F16063' />}
              onClick={handelConfirm}
              disabled={loading}
              loading={loading}
              data-testid={`${dataTestId}-dialog-ok`}
            >
              {t('common:removeIt')}
            </UiButton>
            <UiButton
              variant='text'
              autoFocus
              onClick={() => {
                setOpen(false);
                clearData('errorRemove');
              }}
              color='primary'
              disabled={loading}
              data-testid={`${dataTestId}-dialog-cancel`}
            >
              {t('common:cancel')}
            </UiButton>
          </>
        }
        onCancel={() => {
          setOpen(false);
          clearData('errorRemove');
        }}
        onConfirm={() => setOpen(false)}
      >
        {t('common:removeConfirmMessage', {
          title: action?.title,
          count: selected.length,
        })}
        {error && <UiErrors errors={error} />}
      </UiConfirmDialog>
    </>
  );
};
UiTableDeleteOptionModal.propTypes = {
  action: PropTypes.object,
  updateData: PropTypes.func,
  onSuccess: PropTypes.func,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
  children: PropTypes.node,
  selected: PropTypes.array,
};
export default UiTableDeleteOptionModal;
